import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  trackSessionStart,
  trackNextCard,
  trackPreviousCard,
  trackSessionComplete,
  trackThemeToggle,
  trackQRCodeView,
  trackPrintCards,
  trackAppDownloadClick,
  trackSessionRestart,
  trackKeyboardShortcut,
} from "./utils/analytics";

function App() {
  // Session data (includes title, tags, microlearnings, etc.)
  const [sessionData, setSessionData] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [error, setError] = useState(false);
  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);
  // Índices para la sección actual y el chunk dentro de ella
  const [sectionIndex, setSectionIndex] = useState(0);
  const [chunkIndex, setChunkIndex] = useState(0);
  // Estado para mostrar el popup de datos memorizados
  const [showMemorizedPopup, setShowMemorizedPopup] = useState(false);
  const { sessionId } = useParams();
  const [showQRPopup, setShowQRPopup] = useState(false);
  const [showQRBox, setShowQRBox] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved
      ? JSON.parse(saved)
      : window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  // Extracts memorized words (what's between **) for a specific section
  function getMemorizedWordsBySection(sectionIndex, maxChunk) {
    if (!sessionData) return [];
    const wordsSet = new Set();
    const section = sessionData.microlearnings[sectionIndex];

    for (let c = 0; c < maxChunk; c++) {
      const chunk = section.chunks[c];
      const regex = /\*\*(.*?)\*\*/g;
      let match;
      while ((match = regex.exec(chunk)) !== null) {
        wordsSet.add(match[1].trim());
      }
    }
    return Array.from(wordsSet);
  }

  function capitalize(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  // Gets all memorized words, with option to get only seen words or all words
  function getMemorizedWords({ onlySeen = true } = {}) {
    if (!sessionData) return [];
    const allWords = [];

    const maxSection = onlySeen
      ? sectionIndex
      : sessionData.microlearnings.length - 1;

    for (let s = 0; s <= maxSection; s++) {
      const maxChunk =
        onlySeen && s === sectionIndex
          ? chunkIndex + 1
          : sessionData.microlearnings[s].chunks.length;
      const sectionWords = getMemorizedWordsBySection(s, maxChunk);
      allWords.push(...sectionWords);
    }

    return Array.from(new Set(allWords)); // Remove duplicates
  }

  useEffect(() => {
    if (sessionId && sessionData) {
      const totalChunks = sessionData.microlearnings.reduce(
        (sum, section) => sum + section.chunks.length,
        0
      );
      trackSessionStart(sessionId, totalChunks);
    }
  }, [sessionId, sessionData]);

  function toggleTheme() {
    const newTheme = !darkMode;
    setDarkMode(newTheme);
    trackThemeToggle(newTheme ? "dark" : "light");
  }

  const handleKeyPress = useCallback(
    (event) => {
      if (!started) return;
      if (finished) {
        if (event.key.toLowerCase() === "r") {
          trackKeyboardShortcut(sessionId, "restart");
          restart();
        }
      } else {
        if (event.key === " " || event.key === "ArrowRight") {
          event.preventDefault();
          nextChunk();
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          prevChunk();
        }
      }
    },
    [finished, sectionIndex, chunkIndex, sessionData, started]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  // Advances to next chunk; if it's the last of the section, moves to next section
  function nextChunk() {
    if (!sessionData) return;
    const currentSection = sessionData.microlearnings[sectionIndex];
    if (chunkIndex < currentSection.chunks.length - 1) {
      trackKeyboardShortcut(sessionId, "next");
      trackNextCard(sessionId, chunkIndex);
      setChunkIndex(chunkIndex + 1);
    } else {
      if (sectionIndex < sessionData.microlearnings.length - 1) {
        trackKeyboardShortcut(sessionId, "next");
        trackNextCard(sessionId, chunkIndex);
        setSectionIndex(sectionIndex + 1);
        setChunkIndex(0);
      } else {
        setFinished(true);
        const totalChunks = sessionData.microlearnings.reduce(
          (sum, section) => sum + section.chunks.length,
          0
        );
        trackSessionComplete(sessionId, totalChunks);
      }
    }
  }

  // Goes back to previous chunk; if it's the first of the section, returns to last of previous block
  function prevChunk() {
    if (!sessionData) return;
    if (chunkIndex > 0) {
      trackKeyboardShortcut(sessionId, "previous");
      trackPreviousCard(sessionId, chunkIndex - 1);
      setChunkIndex(chunkIndex - 1);
    } else if (sectionIndex > 0) {
      const prevSection = sessionData.microlearnings[sectionIndex - 1];
      trackKeyboardShortcut(sessionId, "previous");
      trackPreviousCard(sessionId, chunkIndex - 1);
      setSectionIndex(sectionIndex - 1);
      setChunkIndex(prevSection.chunks.length - 1);
    }
  }

  // Restarts the session
  function restart() {
    setSectionIndex(0);
    setChunkIndex(0);
    setFinished(false);
    const totalChunks = sessionData.microlearnings.reduce(
      (sum, section) => sum + section.chunks.length,
      0
    );
    trackSessionRestart(sessionId, totalChunks);
  }

  // Returns the current chunk
  function getCurrentChunk() {
    if (!sessionData) return "";
    return sessionData.microlearnings[sectionIndex].chunks[chunkIndex];
  }

  // Processes markdown highlighting **bold** and *italic*
  function processMarkdown(text) {
    return text
      .replace(
        /\*\*(.*?)\*\*/g,
        (_, content) =>
          `<span class="inline-flex items-center px-2.5 leading-7 pb-1 pt-0.5 mb-1 rounded-lg font-medium 
          bg-teal-100 dark:bg-teal-800/30 text-teal-800 dark:text-teal-100
          border border-teal-200 dark:border-teal-700/30">${content}</span>`
      )
      .replace(/\*(.*?)\*/g, "<em>$1</em>")
      .replace(/\.$/, "");
  }

  // Genera el contenido a mostrar o mensaje de sesión completada
  function getContent() {
    if (finished) {
      return `<div class="flex flex-col items-center gap-4">
        <svg class="w-16 h-16 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
          />
        </svg>
        <div class="text-2xl font-medium text-gray-800 dark:text-gray-200">
          Well done!
        </div>
        <div class="text-gray-600 dark:text-gray-400">
          You've completed the session
        </div>
      </div>`;
    }
    const content = processMarkdown(getCurrentChunk());
    return `<div class="transform transition-all duration-500 ease-in-out 
      ${
        chunkIndex > (window._lastChunkIndex || 0)
          ? "slide-in-up"
          : "slide-in-down"
      }
      leading-9">${content}</div>`;
  }

  useEffect(() => {
    window._lastChunkIndex = chunkIndex;
  }, [chunkIndex]);

  // Buttons with spacing relative to the card
  function getBottom() {
    if (finished) {
      return (
        <div className="flex gap-4 justify-center mt-8">
          <button
            className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 
            duration-300 rounded-xl px-8 py-3 text-white font-medium shadow-lg 
            hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            onClick={restart}
          >
            Restart
          </button>
        </div>
      );
    }
    const currentSection = sessionData.microlearnings[sectionIndex];
    const isAtLastChunk =
      sectionIndex === sessionData.microlearnings.length - 1 &&
      chunkIndex === currentSection.chunks.length - 1;

    return (
      <div className="flex flex-col items-center gap-4 mt-8">
        <div className="flex gap-4">
          {(sectionIndex > 0 || chunkIndex > 0) && (
            <button
              onClick={prevChunk}
              className="bg-gradient-to-r from-gray-600 to-gray-500 hover:from-gray-500 hover:to-gray-400 
              duration-300 rounded-xl px-6 py-3 text-white font-medium shadow-lg 
              hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            >
              Previous
            </button>
          )}
          <button
            className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 
            duration-300 rounded-xl px-8 py-3 text-white font-medium shadow-lg 
            hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            onClick={() => {
              if (isAtLastChunk) {
                setFinished(true);
                const totalChunks = sessionData.microlearnings.reduce(
                  (sum, section) => sum + section.chunks.length,
                  0
                );
                trackSessionComplete(sessionId, totalChunks);
              } else {
                nextChunk();
              }
            }}
          >
            {isAtLastChunk ? "Finish" : "Next"}
          </button>
        </div>
        {/* Button to show memorized data popup */}
        <button
          onClick={() => setShowMemorizedPopup(true)}
          className="mt-2 bg-white dark:bg-gray-800 border border-teal-200 dark:border-teal-400 rounded-xl px-4 py-2 text-teal-600 dark:text-teal-400 font-medium shadow-md hover:shadow-lg transition-all"
        >
          View memorized data
        </button>
      </div>
    );
  }

  // Petición a la API
  useEffect(() => {
    if (sessionId) {
      fetch("https://microlearning-api.widenex.com/session/" + sessionId, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Session expired");
          return res.json();
        })
        .then((data) => {
          setSessionData(data);
          setStarted(true);
        })
        .catch(() => setError(true));
    }
  }, [sessionId]);

  // Content for printing (grouped by sections and chunks)
  function getPrintContent() {
    if (!sessionData) return null;
    return (
      <div className="hidden print:block print:p-8">
        <div>
          <h1 className="text-3xl font-bold mb-2 text-left">
            Practice microlearnings
          </h1>
          <p className="text-gray-600 mb-6 text-left">
            Fill in the blanks to test your knowledge
          </p>
          {sessionData.microlearnings.map((section, sIdx) => (
            <div key={`practice-section-${sIdx}`} className="mb-6">
              <h2 className="text-xl font-semibold mb-2">{section.title}</h2>
              <ul className="list-none space-y-4">
                {section.chunks.map((chunk, cIdx) => (
                  <li
                    key={`practice-${sIdx}-${cIdx}`}
                    className="text-left flex items-start gap-3"
                  >
                    <div className="w-5 h-5 mt-1 flex-shrink-0 border-2 border-gray-400 rounded-sm"></div>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: chunk
                          .replace(
                            /{(.*?)}/g,
                            (_, content) =>
                              `<span class="mx-0.5">${"_".repeat(
                                Math.max(4, content.length)
                              )}</span>`
                          )
                          .replace(
                            /\*\*(.*?)\*\*/g,
                            (_, content) =>
                              `<span class="mx-0.5">${"_".repeat(
                                Math.max(4, content.length)
                              )}</span>`
                          ),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="print:break-before-page">
          <h1 className="text-3xl font-bold mb-2 text-left">
            Answered microlearnings
          </h1>
          <p className="text-gray-600 mb-6 text-left">
            Review sheet with highlighted answers
          </p>
          {sessionData.microlearnings.map((section, sIdx) => (
            <div key={`answer-section-${sIdx}`} className="mb-6">
              <h2 className="text-xl font-semibold mb-2">{section.title}</h2>
              <ul className="list-none space-y-4">
                {section.chunks.map((chunk, cIdx) => (
                  <li
                    key={`answer-${sIdx}-${cIdx}`}
                    className="text-left flex items-start gap-3"
                  >
                    <div className="w-5 h-5 mt-1 flex-shrink-0 border-2 border-gray-400 rounded-sm"></div>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: chunk
                          .replace(
                            /{(.*?)}/g,
                            (_, content) =>
                              `<strong class="text-teal-700 print:text-black">${content}</strong>`
                          )
                          .replace(
                            /\*\*(.*?)\*\*/g,
                            (_, content) =>
                              `<strong class="text-teal-700 print:text-black">${content}</strong>`
                          ),
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function handlePrint() {
    const totalChunks = sessionData.microlearnings.reduce(
      (sum, section) => sum + section.chunks.length,
      0
    );
    trackPrintCards(sessionId, totalChunks);
    window.print();
  }

  function getQRSection() {
    return (
      <>
        <div
          className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 print:hidden hidden md:block
            transition-all duration-500 ease-in-out origin-bottom ${
              showQRBox
                ? "translate-y-0 opacity-100"
                : "translate-y-full opacity-0"
            }`}
        >
          {/* <button
            onClick={() => setShowQRBox(false)}
            className="absolute -top-3 right-3 z-10
              w-6 h-6 flex items-center justify-center
              hover:bg-gray-200/50 dark:hover:bg-gray-700/50
              rounded-full transition-colors"
            aria-label="Close QR box"
          >
            <svg
              className="w-4 h-4 text-gray-600 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button> */}
          {/* <div
            className="bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm 
            rounded-xl p-4 flex gap-4 items-center max-w-md mx-auto
            shadow-lg"
          >
            <QRCodeSVG
              value={window.location.href}
              size={92}
              bgColor={"transparent"}
              fgColor={"currentColor"}
              className="text-gray-800 pt-1 dark:text-gray-200 flex-shrink-0"
              level={"L"}
            />
            <div className="flex flex-col justify-center gap-3">
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                To save these learnings for the future:
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">1</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Download the{" "}
                  <a
                    href="https://apps.apple.com/es/app/summarizer-local/id6738142313"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAppDownloadClick("ios")}
                    className="inline-flex items-center gap-1 px-2 py-0.5
                      bg-gradient-to-r from-teal-500/10 to-teal-600/10 
                      dark:from-teal-500/20 dark:to-teal-400/20
                      hover:from-teal-500/20 hover:to-teal-600/20 
                      dark:hover:from-teal-500/30 dark:hover:to-teal-400/30
                      border border-teal-200/30 dark:border-teal-400/20
                      rounded-full shadow-sm backdrop-blur-sm
                      transform hover:scale-105 transition-all duration-300
                      text-teal-600 dark:text-teal-400 font-medium"
                  >
                    Summarizer GPT
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </a>{" "}
                  App
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gray-800 dark:bg-gray-700 flex items-center justify-center">
                  <span className="text-white text-xs font-medium">2</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Scan this{" "}
                  <button
                    onClick={() => handleQRCodeClick()}
                    className="inline-flex items-center gap-1 px-2 py-0.5
                      bg-gradient-to-r from-teal-500/10 to-teal-600/10 
                      dark:from-teal-500/20 dark:to-teal-400/20
                      hover:from-teal-500/20 hover:to-teal-600/20 
                      dark:hover:from-teal-500/30 dark:hover:to-teal-400/30
                      border border-teal-200/30 dark:border-teal-400/20
                      rounded-full shadow-sm backdrop-blur-sm
                      transform hover:scale-105 transition-all duration-300
                      text-teal-600 dark:text-teal-400 font-medium"
                  >
                    QR code
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </button>
                </p>
              </div>
            </div>
          </div> */}
        </div>
        {/* {!showQRBox && (
          <button
            onClick={() => setShowQRBox(true)}
            className="fixed bottom-6 right-6 print:hidden hidden md:flex items-center gap-2
              bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm 
              rounded-xl p-2 shadow-lg
              hover:bg-white/70 dark:hover:bg-gray-800/70 transition-colors
              transform hover:scale-105 duration-300"
          >
            <QRCodeSVG
              value={window.location.href}
              size={32}
              bgColor={"transparent"}
              fgColor={"currentColor"}
              className="text-gray-800 dark:text-gray-200"
              level={"L"}
            />
            <svg
              className="w-4 h-4 text-gray-600 dark:text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          </button>
        )} */}
      </>
    );
  }

  function handleQRCodeClick() {
    trackQRCodeView(sessionId);
    setShowQRPopup(true);
  }

  function handleAppDownloadClick(platform) {
    trackAppDownloadClick(platform);
  }

  if (!started) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto p-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-6">
              {sessionData ? sessionData.title : "Loading..."}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
              Ready to start your learning session?
            </p>
            <button
              onClick={() => setStarted(true)}
              className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 
              duration-300 rounded-xl px-10 py-4 text-white text-lg font-medium shadow-lg 
              hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
            >
              Start Learning
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900">
      <style>
        {`
          @keyframes slideInUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slideInDown {
            from {
              transform: translateY(-100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          .slide-in-up {
            animation: slideInUp 0.5s ease-out;
          }
          
          .slide-in-down {
            animation: slideInDown 0.5s ease-out;
          }
        `}
      </style>
      <div className="text-center fixed inset-0 p-6 md:p-10 flex flex-col gap-8 items-center justify-center max-w-2xl mx-auto print:static print:p-0 print:max-w-none">
        <a
          href="https://chatgpt.com/g/g-uefFoRnpX"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 left-4 flex items-center gap-2 px-3 py-2 
            bg-gradient-to-r from-teal-500/10 to-teal-600/10 dark:from-teal-500/20 dark:to-teal-400/20
            hover:from-teal-500/20 hover:to-teal-600/20 dark:hover:from-teal-500/30 dark:hover:to-teal-400/30
            border border-teal-200/30 dark:border-teal-400/20
            rounded-full shadow-sm backdrop-blur-sm
            transform hover:scale-105 transition-all duration-300
            print:hidden"
        >
          <img src="/logo.png" alt="Logo" className="w-6 h-6 rounded-full" />
          <span className="text-gray-700 dark:text-gray-200 font-medium">
            Summarizer GPT
          </span>
        </a>
        <button
          onClick={toggleTheme}
          className="absolute top-4 right-4 p-2 rounded-lg bg-gray-200 dark:bg-gray-700 
            hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors print:hidden"
          aria-label="Toggle theme"
        >
          {darkMode ? (
            <svg
              className="w-6 h-6 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
          )}
        </button>
        {(!isMobile || isIOS) && (
          <button
            onClick={
              isIOS
                ? () =>
                    (window.location.href =
                      "https://apps.apple.com/es/app/summarizer-local/id6738142313")
                : handlePrint
            }
            className="absolute top-4 right-16 p-2 rounded-lg bg-gray-200 dark:bg-gray-700 
              hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors print:hidden"
            aria-label={isIOS ? "Download app" : "Print microlearnings"}
          >
            <svg
              className="w-6 h-6 text-gray-700 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isIOS ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              )}
            </svg>
          </button>
        )}
        <div className="print:hidden w-full">
          {/* Títulos: lección y sección */}
          <div className="mb-4">
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-200 mb-14">
              {sessionData.title}
            </h1>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mt-2">
              {sessionData.microlearnings[sectionIndex].title}
            </h2>
            {/* Paginación estilo barra para la sección actual */}
            <div className="flex gap-2 mx-10 mt-4">
              {sessionData.microlearnings[sectionIndex].chunks.map((_, idx) => (
                <div
                  key={idx}
                  className={`flex-1 h-2 rounded transition-all duration-300 ${
                    idx <= chunkIndex
                      ? "bg-teal-600"
                      : "bg-gray-200 dark:bg-gray-700"
                  }`}
                ></div>
              ))}
            </div>
          </div>
          {/* Tarjeta de contenido */}
          <div className="flex-1 flex flex-col items-center justify-center">
            <div
              className="bg-white dark:bg-gray-800 h-[300px] rounded-2xl shadow-lg p-8 md:p-10 w-full max-w-xl mx-auto
              transform transition-all duration-500 ease-in-out flex flex-col justify-center"
            >
              <p
                className="text-xl md:text-2xl text-gray-800 dark:text-gray-200 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: getContent() }}
              ></p>
            </div>
          </div>
          {getBottom()}
        </div>
        {getPrintContent()}
      </div>
      {getQRSection()}
      {/* Popup to show memorized data */}
      {showMemorizedPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 md:p-8 relative max-h-[60vh] max-w-2xl w-full overflow-y-auto">
            <button
              onClick={() => setShowMemorizedPopup(false)}
              className="absolute top-4 right-4 text-gray-600 dark:text-gray-500 hover:text-gray-800 dark:hover:text-gray-100"
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-4">
              Memorized Data
            </h3>
            <div className="mb-4">
              <label
                htmlFor="sectionFilter"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Filter by section:
              </label>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => {
                    setSelectedSection(null);
                    getMemorizedWords({ onlySeen: true });
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedSection === null
                      ? "bg-gray-600 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Seen so far
                </button>
                {/* <button
                  onClick={() => {
                    setSelectedSection(null);
                    getMemorizedWords({ onlySeen: false });
                  }}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedSection === null
                      ? "bg-gray-600 text-white"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  All sections
                </button> */}
                {sessionData?.microlearnings?.map((section, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedSection(idx)}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedSection === idx
                        ? "bg-gray-600 text-white"
                        : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </div>
            <div className="">
              {(selectedSection === null
                ? getMemorizedWords()
                : getMemorizedWordsBySection(
                    selectedSection,
                    sessionData.microlearnings[selectedSection].chunks.length
                  )
              ).map((word, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center pt-1 mb-1 mr-1 px-2.5 h-8 pb-1 rounded-lg font-medium bg-teal-100 dark:bg-teal-800/30 text-teal-800 dark:text-teal-100 border border-teal-200 dark:border-teal-700/30"
                >
                  {capitalize(word)}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
